import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Pin, Plus, Megaphone } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, EmptyState } from "@/components/app/PageKit";

export default function Announcements() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", pinned: false, audience: "all" });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/announcements");
      setItems(Array.isArray(r.data) ? r.data : []);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load announcements");
      setItems([]);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/announcements", form);
      toast.success("Published");
      setOpen(false);
      setForm({ title:"", body:"", pinned:false, audience:"all"});
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const canPost = ["super_admin", "hr_admin", "admin_ops"].includes(user?.role);

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5 max-w-full overflow-x-hidden" data-testid="announcements-page">
      <PageHeader
        category="Knowledge"
        title="Announcements"
        description={user?.role === "employee" ? "Company-wide updates and notices for you" : "Publish updates · pin important news · target audiences"}
        primaryAction={canPost ? {
          label: "New Announcement",
          icon: Plus,
          onClick: () => setOpen(true),
          testid: "new-announcement-btn",
        } : undefined}
      />

      {canPost && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>New Announcement</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} className="mt-1" data-testid="ann-title"/></div>
              <div><Label>Body</Label><Textarea value={form.body} onChange={(e) => setForm({...form, body: e.target.value})} className="mt-1" rows={4} data-testid="ann-body"/></div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.pinned} onChange={(e) => setForm({...form, pinned: e.target.checked})}/> Pin this announcement</label>
            </div>
            <DialogFooter><button onClick={create} className="qh-btn btn-primary" data-testid="ann-publish">Publish</button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {loading ? (
        <Card className="p-10 text-center border-slate-200">
          <div className="animate-pulse text-slate-400">Loading announcements…</div>
        </Card>
      ) : error ? (
        <EmptyState
          icon={Megaphone}
          title="Couldn't load announcements"
          message={error}
        />
      ) : items.length === 0 ? (
        <EmptyState
          icon={Megaphone}
          title="No announcements yet"
          message={canPost
            ? "Publish your first company-wide update to keep everyone aligned."
            : "When HR posts updates, you'll see them here first."}
          action={canPost ? { label: "Publish first update", icon: Plus, onClick: () => setOpen(true) } : undefined}
          testid="ann-empty"
        />
      ) : (
        <div className="space-y-3">
          {items.map((a) => (
            <Card key={a.id} className={`p-5 border-slate-200 shadow-sm bg-white ${a.pinned ? "border-l-4 border-l-violet-500" : ""}`} data-testid={`ann-${a.id}`}>
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg qh-grad-bg text-white flex items-center justify-center shrink-0">
                  <Megaphone className="w-4 h-4"/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-[Outfit] font-bold text-slate-900">{a.title || "Untitled"}</h3>
                    {a.pinned && <Badge className="bg-violet-100 text-violet-700 border-0 text-[10px]"><Pin className="w-3 h-3 mr-1"/>Pinned</Badge>}
                  </div>
                  <p className="text-sm text-slate-600 mt-1 leading-relaxed whitespace-pre-wrap">{a.body || a.content || ""}</p>
                  <div className="text-xs text-slate-400 mt-2">
                    By {a.author?.full_name || a.created_by_name || "Admin"} · {a.created_at ? new Date(a.created_at).toLocaleString() : ""}
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
